import { useCallback, useEffect, useState } from "react";
import {
  getPreferences, putPreferences,
  type DigestCadence, type NotificationChannel, type PrefCell, type PreferencesResponse,
  type PrefState,
} from "../api/notificationPrefs";

/**
 * Notification preferences panel (cat-16 row 16.4 — doc 17 §1.4 matrix + 16.9/16.10 setting).
 * Compact vertical layout (fits the w-96 popover host AND the routed page). Rules encoded:
 *  - in-app column is FIXED-ON (greyed, tooltip) — doc 17:74 / plan R14; the resolver ignores
 *    in-app rows anyway, the UI just doesn't lie about it.
 *  - digest state exposes the D-NSiT-02 cadence select (criticals never batch — server-side).
 *  - active window = two HH:MM selects; timezone defaults to the BROWSER tz (no IANA picker in
 *    v1 — plan bound); vacation = date input on vacation_until + clear.
 * Save = ONE transactional PUT (full-matrix replace server-side); on failure the panel reloads
 * server truth so the UI never diverges from a partial write (AC-11).
 */
const CHANNELS: NotificationChannel[] = ["in-app", "email", "teams"];
const STATES: PrefState[] = ["on", "off", "digest"];
const CADENCES: DigestCadence[] = ["immediate", "15m", "hourly", "daily", "weekly"];

type ViewState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready" };

export interface NotificationPrefsPanelProps {
  apiBase?: string;
  fetchImpl?: typeof fetch;
}

type CellKey = `${string}|${string}`;
const keyOf = (category: string, channel: string): CellKey => `${category}|${channel}`;

export function NotificationPrefsPanel({
  apiBase = "/api/workspace",
  fetchImpl,
}: NotificationPrefsPanelProps): React.ReactElement {
  const f = fetchImpl ?? fetch;
  const [view, setView] = useState<ViewState>({ kind: "loading" });
  const [defaults, setDefaults] = useState<Record<string, NotificationChannel[]>>({});
  const [cells, setCells] = useState<Map<CellKey, PrefCell>>(new Map());
  const [setting, setSetting] = useState<PreferencesResponse["setting"]>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const apply = useCallback((data: PreferencesResponse) => {
    setDefaults(data.defaults);
    setCells(new Map(data.matrix.map((c) => [keyOf(c.category, c.channel), c])));
    setSetting(data.setting);
  }, []);

  const load = useCallback(async () => {
    setView({ kind: "loading" });
    try {
      apply(await getPreferences(f, apiBase));
      setView({ kind: "ready" });
    } catch (e) {
      setView({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
    // f/apiBase are props-derived and stable for the panel's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const cellState = (category: string, channel: NotificationChannel): PrefCell => {
    const explicit = cells.get(keyOf(category, channel));
    if (explicit) return explicit;
    const on = (defaults[category] ?? []).includes(channel);
    return { category, channel, state: on ? "on" : "off" };
  };

  const setCell = (category: string, channel: NotificationChannel, patch: Partial<PrefCell>) => {
    setCells((prev) => {
      const next = new Map(prev);
      next.set(keyOf(category, channel), { ...cellState(category, channel), ...patch });
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      // Full-matrix replace: every non-in-app cell we know about (explicit or edited).
      const matrix = [...cells.values()].filter((c) => c.channel !== "in-app");
      apply(await putPreferences({ matrix, setting }, f, apiBase));
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
      await load();   // reload server truth — never leave a diverged optimistic state (AC-11)
    } finally {
      setSaving(false);
    }
  };

  if (view.kind === "loading") {
    return <div role="status" className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">Loading preferences…</div>;
  }
  if (view.kind === "error") {
    return (
      <div role="alert" className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">
        Couldn’t load preferences: {view.message}
      </div>
    );
  }

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="px-3 py-2 flex flex-col gap-3" data-app="notification-prefs">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
          Per-topic channels
        </p>
        <ul className="mt-1 flex flex-col gap-2">
          {Object.keys(defaults).map((category) => (
            <li key={category} className="text-sm">
              <span className="font-medium text-[var(--color-text-primary)]">{category}</span>
              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                {CHANNELS.map((channel) => {
                  const cell = cellState(category, channel);
                  if (channel === "in-app") {
                    return (
                      <span
                        key={channel}
                        title="In-app is always on"
                        className="text-xs rounded border border-[var(--color-border-subtle)] px-1.5 py-0.5 text-[var(--color-text-secondary)] opacity-60"
                      >
                        in-app: on
                      </span>
                    );
                  }
                  return (
                    <span key={channel} className="flex items-center gap-1 text-xs">
                      <label className="text-[var(--color-text-secondary)]" htmlFor={`${category}-${channel}`}>
                        {channel}
                      </label>
                      <select
                        id={`${category}-${channel}`}
                        value={cell.state}
                        onChange={(e) => setCell(category, channel, {
                          state: e.target.value as PrefState,
                          digest_cadence: e.target.value === "digest" ? (cell.digest_cadence ?? "daily") : null,
                        })}
                        className="rounded border border-[var(--color-border-subtle)] bg-transparent px-1 py-0.5"
                      >
                        {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {cell.state === "digest" && (
                        <select
                          aria-label={`${category} ${channel} digest cadence`}
                          value={cell.digest_cadence ?? "daily"}
                          onChange={(e) => setCell(category, channel, { digest_cadence: e.target.value as DigestCadence })}
                          className="rounded border border-[var(--color-border-subtle)] bg-transparent px-1 py-0.5"
                        >
                          {CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      )}
                    </span>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
          Active hours &amp; away
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          <label htmlFor="win-start" className="text-[var(--color-text-secondary)]">Pings from</label>
          <input
            id="win-start" type="time" value={setting.active_window_start ?? ""}
            onChange={(e) => setSetting((s) => ({
              ...s,
              active_window_start: e.target.value || null,
              active_window_end: e.target.value ? (s.active_window_end ?? "17:00") : null,
              timezone: s.timezone ?? browserTz,
            }))}
            className="rounded border border-[var(--color-border-subtle)] bg-transparent px-1 py-0.5"
          />
          <label htmlFor="win-end" className="text-[var(--color-text-secondary)]">to</label>
          <input
            id="win-end" type="time" value={setting.active_window_end ?? ""}
            disabled={!setting.active_window_start}
            onChange={(e) => setSetting((s) => ({
              ...s,
              // Window is all-or-nothing (backend CHECK): clearing the end clears the start too —
              // a half-open window must never reach the PUT (review Critical).
              active_window_end: e.target.value || null,
              active_window_start: e.target.value ? s.active_window_start : null,
            }))}
            className="rounded border border-[var(--color-border-subtle)] bg-transparent px-1 py-0.5 disabled:opacity-50"
          />
          <span className="text-[var(--color-text-secondary)]">({setting.timezone ?? browserTz})</span>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
          <label htmlFor="vacation" className="text-[var(--color-text-secondary)]">Away until</label>
          <input
            id="vacation" type="date"
            value={setting.vacation_until ? setting.vacation_until.slice(0, 10) : ""}
            onChange={(e) => setSetting((s) => ({
              ...s,
              vacation_until: e.target.value ? `${e.target.value}T00:00:00Z` : null,
            }))}
            className="rounded border border-[var(--color-border-subtle)] bg-transparent px-1 py-0.5"
          />
          {setting.vacation_until && (
            <button
              type="button"
              onClick={() => setSetting((s) => ({ ...s, vacation_until: null }))}
              className="text-[var(--color-accent)] hover:underline"
            >
              Clear
            </button>
          )}
          <label className="ml-2 flex items-center gap-1 text-[var(--color-text-secondary)]">
            <input
              type="checkbox"
              checked={setting.urgency_bypass_allowed ?? true}
              onChange={(e) => setSetting((s) => ({ ...s, urgency_bypass_allowed: e.target.checked }))}
            />
            Urgent messages may bypass my quiet hours
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded bg-[var(--color-accent)] px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save preferences"}
        </button>
        {saveError && (
          <span role="alert" className="text-xs text-[var(--color-danger,#c62828)]">
            Couldn’t save: {saveError} (showing server state)
          </span>
        )}
      </div>
    </div>
  );
}
